import React from 'react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Plus, Trash2 } from 'lucide-react';

export const EMPTY_EMAIL = { email: '', label: '', primary: false };
export const EMPTY_ADDRESS = { label: '', street: '', street2: '', city: '', state: '', zip: '', primary: false };

const EMAIL_LABELS = ['Home', 'Work', 'Other'];
const ADDRESS_LABELS = ['Home', 'Work', 'Seasonal', 'Mailing', 'Other'];

export const formatAddress = (a) => [
  [a.street, a.street2].filter(Boolean).join(', '),
  a.city,
  [a.state, a.zip].filter(Boolean).join(' '),
].filter(Boolean).join(', ');

export function EmailListEditor({ emails, onChange }) {
  const update = (i, patch) => onChange(emails.map((e, j) => (j === i ? { ...e, ...patch } : e)));
  const setPrimary = (i) => onChange(emails.map((e, j) => ({ ...e, primary: j === i })));
  const remove = (i) => onChange(emails.filter((_, j) => j !== i));
  return (
    <div className="space-y-1.5" data-testid="email-list-editor">
      {emails.map((e, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <input type="radio" checked={!!e.primary} onChange={() => setPrimary(i)}
                 title="Primary email" className="shrink-0 accent-[#6B3FA0]" data-testid={`email-primary-${i}`} />
          <Input type="email" placeholder="email@example.com" value={e.email}
                 onChange={ev => update(i, { email: ev.target.value })}
                 className="flex-1" data-testid={`email-input-${i}`} />
          <select value={e.label || ''} onChange={ev => update(i, { label: ev.target.value })}
                  className="border rounded px-1.5 py-1.5 text-sm w-24" data-testid={`email-label-${i}`}>
            <option value="">Label…</option>
            {EMAIL_LABELS.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
          <Button type="button" variant="ghost" size="sm" onClick={() => remove(i)} data-testid={`email-remove-${i}`}>
            <Trash2 className="h-3.5 w-3.5 text-gray-400" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" data-testid="add-email-btn"
              onClick={() => onChange([...emails, { ...EMPTY_EMAIL, primary: emails.length === 0 }])}>
        <Plus className="h-3.5 w-3.5 mr-1" /> Add email
      </Button>
    </div>
  );
}

export function AddressListEditor({ addresses, onChange }) {
  const update = (i, patch) => onChange(addresses.map((a, j) => (j === i ? { ...a, ...patch } : a)));
  const setPrimary = (i) => onChange(addresses.map((a, j) => ({ ...a, primary: j === i })));
  const remove = (i) => onChange(addresses.filter((_, j) => j !== i));
  return (
    <div className="space-y-2" data-testid="address-list-editor">
      {addresses.map((a, i) => (
        <div key={i} className="border rounded p-2 space-y-1.5 bg-gray-50/60">
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1 text-xs text-gray-600 cursor-pointer">
              <input type="radio" checked={!!a.primary} onChange={() => setPrimary(i)}
                     className="accent-[#6B3FA0]" data-testid={`address-primary-${i}`} /> Primary
            </label>
            <select value={a.label || ''} onChange={ev => update(i, { label: ev.target.value })}
                    className="border rounded px-1.5 py-1 text-xs w-24" data-testid={`address-label-${i}`}>
              <option value="">Label…</option>
              {ADDRESS_LABELS.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
            <div className="flex-1" />
            <Button type="button" variant="ghost" size="sm" onClick={() => remove(i)} data-testid={`address-remove-${i}`}>
              <Trash2 className="h-3.5 w-3.5 text-gray-400" />
            </Button>
          </div>
          <Input placeholder="Street address" value={a.street}
                 onChange={ev => update(i, { street: ev.target.value })} data-testid={`address-street-${i}`} />
          <Input placeholder="Apt / Suite / Line 2 (optional)" value={a.street2}
                 onChange={ev => update(i, { street2: ev.target.value })} data-testid={`address-street2-${i}`} />
          <div className="grid grid-cols-3 gap-1.5">
            <Input placeholder="City" value={a.city} onChange={ev => update(i, { city: ev.target.value })} data-testid={`address-city-${i}`} />
            <Input placeholder="State" value={a.state} onChange={ev => update(i, { state: ev.target.value })} data-testid={`address-state-${i}`} />
            <Input placeholder="ZIP" value={a.zip} onChange={ev => update(i, { zip: ev.target.value })} data-testid={`address-zip-${i}`} />
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" data-testid="add-address-btn"
              onClick={() => onChange([...addresses, { ...EMPTY_ADDRESS, primary: addresses.length === 0 }])}>
        <Plus className="h-3.5 w-3.5 mr-1" /> Add address
      </Button>
    </div>
  );
}
